import { Edit } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { SINGLE_THERAPY_DAY_KEY } from "../../../../../../../enums/QueryKeys";
import { UPDATE_THERAPY_DAY } from "../../../../../../../enums/UrlPaths";
import useSubmit from "../../../../../../master/species/hooks/useSubmit";
import DialogCore from "../../../../../components/core/DialogCore";
import {
  labels,
  NumberInputFieldEditing,
} from "../../../../../components/forms/TherapyDetails";
import { editDateParam } from "../EditTherapyDay";
const formId = "edit_single_therapy_day_form_id";

export default function EditTherapyDayDialog({ therapyDay = null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: therapyId } = useParams();

  const submittion = useSubmit(
    null,
    UPDATE_THERAPY_DAY + therapyDay?.id,
    SINGLE_THERAPY_DAY_KEY + therapyDay?.id,
  );

  const methods = useForm({
    mode: "all",
    defaultValues: {
      morningDoses: therapyDay?.morningDosage ?? null,
      eveningDoses: therapyDay?.eveningDosage ?? null,
      applyFuture: false,
      morningDosageCheckbox: !!therapyDay?.morningDosage,
      eveningDosageCheckbox: !!therapyDay?.eveningDosage,
    },
  });

  const { reset } = methods;

  React.useEffect(() => {
    if (therapyDay) {
      reset({
        morningDoses: therapyDay.morningDosage ?? null,
        eveningDoses: therapyDay.eveningDosage ?? null,
        applyFuture: false,
        morningDosageCheckbox: !!therapyDay.morningDosage,
        eveningDosageCheckbox: !!therapyDay.eveningDosage,
      });
    }
  }, [therapyDay, reset]);

  async function submitData(data) {
    const submitData = {
      eveningDosage: data.eveningDoses || null,
      morningDosage: data.morningDoses || null,
      applyToAll: data.applyFuture,
      therapyId: therapyId,
    };
    const res = await submittion.submitCallback(submitData);

    if (res) {
      queryClient.invalidateQueries([editDateParam]);
    }
  }

  return (
    <FormProvider {...methods}>
      <form id={formId} onSubmit={methods.handleSubmit(submitData)}>
        <DialogCore
          icon={<Edit />}
          buttonSize="small"
          isIcon
          color="white"
          buttonTitle={t("EditTherapy")}
          title={t("EditTherapyDay")}
          maxWidth="md"
          formId={formId}
          closeWhen={submittion.success}
          isLoading={submittion.loading}
        >
          {!therapyDay ? <LinearProgress /> : <FormContent />}
        </DialogCore>
      </form>
    </FormProvider>
  );
}

function FormContent() {
  const { t } = useTranslation();
  const methods = useFormContext();

  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          {`${t("DailyDosage")} (${t("CheckWantedDosage")})`}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          {t("UncheckingTherapyDosageUserHint")}
        </Typography>
        <Stack spacing={2}>
          <NumberInputFieldEditing
            label="Morning"
            helperText="e.g. 15 mg"
            checkboxFieldRegister="morningDosageCheckbox"
            registerTitle={labels.dosageMorning}
          />
          <NumberInputFieldEditing
            label="Evening"
            helperText="e.g. 25 mg"
            checkboxFieldRegister="eveningDosageCheckbox"
            registerTitle={labels.dosageEvening}
          />
        </Stack>
      </Box>
      <Divider />
      <Stack spacing={2}>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) =>
                methods.setValue("applyFuture", e.target.checked)
              }
              checked={methods.watch("applyFuture") || false}
            />
          }
          label={t("ApplyToAllFutureDaysCheckboxLabel")}
        />
        <Typography variant="body1" color={"GrayText"}>
          {t("ApplyToAllFutureDaysCheckboxSubtitle")}
        </Typography>
      </Stack>
    </Stack>
  );
}
